/* eslint-disable max-lines */
/* eslint-disable complexity */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  deleteGcpServiceAccount,
  type GcpServiceAccount,
  gcpServiceAccountQueries,
  type GcpServiceAccountToCreate,
  upsertGcpServiceAccount,
} from '@/lib/queries/gcpServiceAccount';
import type { Project } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';
import { cn } from '@/lib/utils';

import gcpLogo from '../../../-components/logos/gcp.svg?url';
import {
  defaultValues,
  type FormValues,
  updateValidationSchema,
  validationSchema,
} from './validationSchema';

interface Props {
  initialValue?: GcpServiceAccount;
  mainProject: Project;
}

const useFileUpload = (
  form: ReturnType<typeof useForm<FormValues>>,
  toast: ReturnType<typeof useToast>['toast'],
) => {
  const handleFile = useCallback(
    async (file: File) => {
      try {
        const text = await file.text();
        JSON.parse(text); // Validate it's valid JSON

        // Set the service account JSON
        form.setValue('serviceAccountJson', text, { shouldValidate: true });
      } catch {
        toast({
          title: 'configuration.gcpServiceAccount.form.fileUpload.error',
          variant: 'destructive',
        });
      }
    },
    [form, toast],
  );

  return handleFile;
};

export const GcpServiceAccountForm = ({
  initialValue,
  mainProject,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [showPrivateKeyId, setShowPrivateKeyId] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(
      initialValue ? updateValidationSchema : validationSchema,
    ),
    defaultValues: {
      ...defaultValues,
      serviceAccountJson: '', // Always empty since backend doesn't return it
      projectId: initialValue?.jsonProjectId,
    },
  });

  // Reset form when initialValue changes (when navigating to edit a different account)
  useEffect(() => {
    if (initialValue) {
      form.reset({
        serviceAccountJson: '', // Always empty since backend doesn't return it
        projectId: initialValue.jsonProjectId,
      });
    }
  }, [initialValue, form]);

  const handleFile = useFileUpload(form, toast);

  // Watch form values
  const watchedServiceAccountJson = form.watch('serviceAccountJson');

  // Determine if create button should be disabled
  const isCreateDisabled =
    !initialValue && watchedServiceAccountJson.trim() === '';

  // Update button is never disabled (optional JSON for updates)
  const isUpdateDisabled = false;

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file?.type === 'application/json') {
        await handleFile(file);
      }
    },
    [handleFile],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
    },
    maxFiles: 1,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDropAccepted: () => setIsDragging(false),
    onDropRejected: () => setIsDragging(false),
  });

  const { isPending, mutateAsync } = useMutation({
    mutationKey: generateMutationKey(
      gcpServiceAccountQueries.byProject(mainProject.id),
      'upsert',
      true,
    ),
    mutationFn: async (values: FormValues) => {
      let projectIdToSend = initialValue?.jsonProjectId;

      // Only parse JSON if it's provided (for create or when updating credentials)
      const hasServiceAccountJson = values.serviceAccountJson !== '';

      if (hasServiceAccountJson) {
        const parsedJson = JSON.parse(values.serviceAccountJson) as {
          project_id: string;
        };
        projectIdToSend = parsedJson.project_id;
      }

      const payload: GcpServiceAccountToCreate = {};

      if (hasServiceAccountJson) {
        payload.serviceAccountJson = values.serviceAccountJson;
      }

      if (projectIdToSend !== undefined) {
        payload.projectId = projectIdToSend;
      }

      await upsertGcpServiceAccount(
        mainProject.id,
        payload,
        initialValue?.accountId,
      );
    },
    onSuccess: async () => {
      router.navigate({ to: '/integrations/gcp/service-accounts' });
    },
    onError: error => {
      console.error('GCP Service Account error:', error);
      toast({
        title:
          initialValue !== undefined
            ? 'configuration.gcpServiceAccount.form.update.error'
            : 'configuration.gcpServiceAccount.form.create.error',
        variant: 'destructive',
      });
    },
  });

  const { isPending: isDeleting, mutateAsync: deleteMutation } = useMutation({
    mutationKey: generateMutationKey(
      gcpServiceAccountQueries.list(mainProject.id),
      'delete',
      true,
    ),
    mutationFn: async (accountId: string) => {
      await deleteGcpServiceAccount(mainProject.id, accountId);
    },
    onSuccess: async () => {
      toast({
        title: 'configuration.gcpServiceAccount.form.delete.success',
        variant: 'default',
      });
      router.navigate({ to: '/integrations/gcp/service-accounts' });
    },
    onError: () => {
      toast({
        title: 'configuration.gcpServiceAccount.form.delete.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(async values => mutateAsync(values))}>
        <h1 className="text-3xl mb-6 flex items-center gap-3">
          <img src={gcpLogo} alt="GCP" className="h-8 w-8" />
          <FormattedMessage id="configuration.gcpServiceAccount.form.manageCredentials" />
        </h1>

        {/* API Note */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong>
              <FormattedMessage id="integrations.gcp.apiNote.title" />
            </strong>{' '}
            <FormattedMessage id="integrations.gcp.apiNote.description" />{' '}
            <FormattedMessage
              id="integrations.gcp.apiNote.documentation"
              values={{
                a: (chunks: React.ReactNode) => (
                  <a
                    href="https://docs.anyshift.io/pages/integration/gcp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-blue-600 dark:hover:text-blue-300"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </p>
        </div>

        {/* Display existing service account metadata when editing */}
        {initialValue && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.title" />
            </h2>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.field" />
                    </TableHead>
                    <TableHead>
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.value" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.projectId" />
                    </TableCell>
                    <TableCell className="font-mono">
                      {initialValue.jsonProjectId}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.clientEmail" />
                    </TableCell>
                    <TableCell className="font-mono text-sm break-all">
                      {initialValue.clientEmail}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.clientId" />
                    </TableCell>
                    <TableCell className="font-mono">
                      {initialValue.clientId}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.privateKeyId" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">
                          {showPrivateKeyId
                            ? initialValue.privateKeyId
                            : '••••••••••••••••'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setShowPrivateKeyId(!showPrivateKeyId)}
                        >
                          {showPrivateKeyId ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.status" />
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                          initialValue.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                        )}
                      >
                        {initialValue.status}
                      </span>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      <FormattedMessage id="configuration.gcpServiceAccount.form.currentDetails.lastUpdated" />
                    </TableCell>
                    <TableCell>
                      {new Date(initialValue.updatedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6">
          <h2 className="text-xl font-semibold">
            <FormattedMessage
              id={
                initialValue
                  ? 'configuration.gcpServiceAccount.form.updateDetails'
                  : 'configuration.gcpServiceAccount.form.enterDetails'
              }
            />
          </h2>
          <div className="flex flex-col gap-4 max-w-2xl">
            <FormField
              control={form.control}
              name="serviceAccountJson"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    <FormattedMessage id="configuration.gcpServiceAccount.form.serviceAccountJson.label" />
                    {initialValue && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        <FormattedMessage id="configuration.gcpServiceAccount.form.serviceAccountJson.optional" />
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      <div
                        {...getRootProps()}
                        className={cn(
                          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                          isDragging
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500',
                        )}
                      >
                        <input {...getInputProps()} />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <FormattedMessage id="configuration.gcpServiceAccount.form.fileUpload.dropzone" />
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          <FormattedMessage id="configuration.gcpServiceAccount.form.fileUpload.hint" />
                        </p>
                      </div>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            <FormattedMessage id="configuration.gcpServiceAccount.form.or" />
                          </span>
                        </div>
                      </div>

                      <Textarea
                        {...field}
                        className="min-h-[200px] font-mono text-xs"
                        placeholder={formatMessage({
                          id: 'configuration.gcpServiceAccount.form.serviceAccountJson.placeholder',
                        })}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {initialValue ? (
                      <FormattedMessage id="configuration.gcpServiceAccount.form.serviceAccountJson.updateHelp" />
                    ) : (
                      <FormattedMessage
                        id="configuration.gcpServiceAccount.form.serviceAccountJson.help"
                        values={{
                          a: (chunks: React.ReactNode) => (
                            <a
                              href="https://cloud.google.com/iam/docs/service-accounts-create"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              {chunks}
                            </a>
                          ),
                        }}
                      />
                    )}
                  </p>
                </FormItem>
              )}
            />

            <div className="flex flex-row gap-4 justify-between">
              {initialValue !== undefined ? (
                <>
                  <Button
                    variant="main"
                    disabled={isPending || isUpdateDisabled}
                    type="submit"
                  >
                    {isPending ? (
                      <FormattedMessage id="configuration.gcpServiceAccount.form.update.pending" />
                    ) : (
                      <FormattedMessage id="configuration.gcpServiceAccount.form.update.label" />
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    className="bg-red-500 hover:bg-red-600"
                    disabled={isDeleting}
                    onClick={() => {
                      deleteMutation(initialValue.accountId);
                    }}
                    type="button"
                  >
                    {isDeleting ? (
                      <FormattedMessage id="configuration.gcpServiceAccount.form.delete.pending" />
                    ) : (
                      <FormattedMessage id="configuration.gcpServiceAccount.form.delete.label" />
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  variant="main"
                  disabled={isPending || isCreateDisabled}
                  type="submit"
                >
                  {isPending ? (
                    <FormattedMessage id="configuration.gcpServiceAccount.form.create.pending" />
                  ) : (
                    <FormattedMessage id="configuration.gcpServiceAccount.form.create.label" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
